<template>
    <div class="impact-brief-wrapper">
        <impact-brief-wizard
            :is-loading="isLoading"
            :visible="showImpactBriefWizard"
            :active-step="activeStep"
            :steps="steps"
            :disable-next-button="isNextButtonDisabled"
            @next-step="handleNextStep"
            @prev-step="handlePrevStep"
            @exit-clicked="handleExitClicked">
            <template #body>
                <component
                    :is="wizardBodyComponent"
                    v-if="wizardBodyComponent"
                    :form-data="formData"
                    :events-with-limited-data="eventsWithLimitedData"
                    :impact-brief-preview="impactBriefPreview"
                    @form-validation="handleFormValidation"
                    @update-form-data="handleUpdateFormData"
                    @measure-type="handleMeasureTypeChange"
                    @next-step="handleNextStep"
                    @run-onboarded-visitor-agg="eventsDataCheck" />
            </template>
        </impact-brief-wizard>
    </div>
</template>
<script>
import ImpactBriefWizard from './impact-brief-wizard.vue';
import { fetchPossiblePeriods } from '@/utils/time-series.utils';
import { runAggregationOnce } from '@/utils/aggregation';
import TotalVisitorsAndAccountsInfluencedAggregation from '@/impact-brief/aggregations/total-visitors-and-accounts-influenced.aggregation.ts';
import OnboardingTimeToCompleteAggregation from './aggregations/onboarding-time-to-complete.aggregation';
import TotalOnboardedAggregation from './aggregations/total-onboarded.aggregation';
import PercentageOnboardedAggregation from './aggregations/percentage-onboarded.aggregation';
import ImpactBriefGuideMetricsAgg from '@/impact-brief/aggregations/impact-brief-guide-metrics.aggregation.ts';
import OnboardingRetentionAggregation from '@/impact-brief/aggregations/onboarding-retention.aggregation.ts';
import GenerateBrief from '@/impact-brief/components/generate-brief.vue';
import SelectInitiatives from './components/select-initiatives.vue';
import ImpactBriefConfig from './views/impact-brief-config.vue';
import { generateBlob } from '@/utils/docx/generate-impact-brief.docx';
import { PendoNotification } from '@pendo/components';
import { mapState } from 'vuex';
import camelCase from 'lodash/camelCase';
import { roundPercentage } from '@/utils/formatters';
import { smartFormat, formatSmartMoney, formatTimeToComplete } from '@/impact-brief/utils/formatting.utils';
import { IMPACT_BRIEF_COST_SAVINGS, SUGGESTED_ACTIONS_THRESHOLDS } from './constants/impact-brief.constants';
import { saveFile } from '@/utils/docx/generate-docx.utils';
import { cancelPendingAggregations } from '@pendo/agg-connect';

export default {
    name: 'ImpactBriefWrapper',
    components: {
        ImpactBriefWizard,
        SelectInitiatives,
        GenerateBrief,
        ImpactBriefConfig,
    },
    props: {
        showImpactBriefWizard: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['hideWizard'],
    data () {
        return {
            activeStep: 'configure',
            steps: [
                { name: 'configure', label: 'Configure brief' },
                { name: 'preview', label: 'Preview brief' },
            ],
            formData: {},
            isLoading: false,
            isFormValid: false,
            briefFirstPeriodNumDays: 0,
            numberOfPositiveMetrics: 0,
            eventsWithLimitedData: [],
            impactBriefPreview: null,
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            subscriptionDisplayName: (state) => state.subscription.active.displayName,
        }),
        userName () {
            return `${this.user.first} ${this.user.last}`;
        },
        wizardBodyComponent () {
            switch (this.activeStep) {
                case 'configure':
                    return 'impact-brief-config';
                case 'preview':
                    return 'generate-brief';
                default:
                    return '';
            }
        },
        isNextButtonDisabled () {
            return this.activeStep === 'configure' && !this.isFormValid;
        },
        usesAccountsAggregation () {
            return this.formData.measureType === 'accounts';
        },
    },
    watch: {
        async activeStep (step) {
            if (step === 'preview') {
                const preview = await this.generateBriefDocxBlob();
                this.impactBriefPreview = preview;
                this.isLoading = false;
            }
        },
    },
    methods: {
        async handleNextStep () {
            const currentStepIndex = this.steps.findIndex((step) => step.name === this.activeStep);
            const nextStepIndex = currentStepIndex + 1;

            if (nextStepIndex < this.steps.length) {
                this.activeStep = this.steps[nextStepIndex].name;
            } else {
                this.downloadImpactBrief();
            }
        },
        handlePrevStep () {
            const currentStepIndex = this.steps.findIndex((step) => step.name === this.activeStep);
            if (currentStepIndex > 0) {
                this.activeStep = this.steps[currentStepIndex - 1].name;
            }
            this.impactBriefPreview = null;
        },
        handleFormValidation (value) {
            this.isFormValid = value;
        },
        handleUpdateFormData (data) {
            this.formData = data;
        },
        extractGuideData () {
            const allGuideIds = Object.keys(this.formData.guidePolls);
            const filterStrings = allGuideIds.map((id) => `guideId == "${id}"`);
            const combinedFilterString = `(${filterStrings.join(' || ')})`;
            const guideNames = allGuideIds.map((id) => {
                return { name: this.formData.guidePolls[id].name };
            });

            return {
                combinedFilterString,
                guideNames,
            };
        },
        async generateBriefDocxBlob () {
            try {
                this.isLoading = true;
                const {
                    onboardingEvents,
                    briefDateRange,
                    guidePolls,
                    onboardingLength,
                    feedbackResponse,
                    segment,
                } = this.formData;
                const segmentId = segment?.id || null;

                const numEventsSelected = Object.keys(onboardingEvents).length;
                this.onboardingLength = onboardingLength?.value;
                const { count: dayCount } = briefDateRange.spec;
                const { filterPeriodBase, filterPeriodPast, filterPeriodPresent } = this.generateFilterRanges(
                    briefDateRange,
                );

                // Use summarized text if available, otherwise use original text
                const feedbackResponses = feedbackResponse?.summarized
                    ? feedbackResponse.summarizedText
                    : feedbackResponse.text || '';

                const dateRangePeriods = await fetchPossiblePeriods(briefDateRange.spec);
                const { Periods: dateRangePeriodCount } = dateRangePeriods.data;
                this.briefFirstPeriodNumDays = dateRangePeriodCount.length;
                const guidesWithGoals = Object.values(guidePolls).filter((guide) => !!guide.conversion);
                const numberOfGuidesWithGoals = guidesWithGoals.length;
                const { combinedFilterString, guideNames } = this.extractGuideData();

                const totalVisitorsAndAccountsInfluencedAggConfig = {
                    firstComparisonRange: filterPeriodBase,
                    guides: guidesWithGoals.map((guide) => ({
                        guideId: guide.id,
                        conversionItemId: guide.conversion.itemId,
                        conversionItemType: camelCase(guide.conversion.itemType),
                    })),
                };

                const guideMetricsAggConfig = {
                    guideIds: combinedFilterString,
                    startOfPeriod: 'dateAdd(startOfPeriod("daily", now()), -1, "days")',
                    numDays: this.briefFirstPeriodNumDays,
                    blacklist: 'apply',
                };

                const aggregationArray = [runAggregationOnce(ImpactBriefGuideMetricsAgg, guideMetricsAggConfig)];
                // Always last due to the conditional nature of the agg
                if (numberOfGuidesWithGoals) {
                    aggregationArray.push(
                        runAggregationOnce(
                            TotalVisitorsAndAccountsInfluencedAggregation,
                            totalVisitorsAndAccountsInfluencedAggConfig,
                        ),
                    );
                }

                Object.keys(onboardingEvents).forEach((selectedEventId) => {
                    const selectedEvent = onboardingEvents[selectedEventId];
                    const { appId, kind } = selectedEvent;
                    const totalVisitorsAndAccountsAggConfig = {
                        appId,
                        targetType: camelCase(kind),
                        targetId: selectedEventId,
                        firstComparisonRange: filterPeriodBase,
                        secondComparisonRange: filterPeriodPast,
                        thirdComparisonRange: filterPeriodPresent,
                        expectedDaysToOnboard: this.onboardingLength,
                        usesAccounts: this.usesAccountsAggregation,
                        segmentId,
                    };

                    const onboardingTimeToCompleteAggConfig = {
                        appId,
                        targetType: camelCase(kind),
                        targetId: selectedEventId,
                        firstComparisonRange: filterPeriodBase,
                        secondComparisonRange: filterPeriodPast,
                        expectedDaysToOnboard: this.onboardingLength,
                        thirdComparisonRange: filterPeriodPresent,
                        usesAccounts: this.usesAccountsAggregation,
                        segmentId,
                    };

                    const onboardingRetentionAggConfig = {
                        appId,
                        targetType: camelCase(kind),
                        targetId: selectedEventId,
                        startDate: filterPeriodBase,
                        endDate: filterPeriodPresent,
                        usesAccounts: this.usesAccountsAggregation,
                        segmentId,
                    };

                    aggregationArray.push(
                        runAggregationOnce(TotalOnboardedAggregation, totalVisitorsAndAccountsAggConfig),
                        runAggregationOnce(PercentageOnboardedAggregation, totalVisitorsAndAccountsAggConfig),
                        runAggregationOnce(OnboardingTimeToCompleteAggregation, onboardingTimeToCompleteAggConfig),
                        runAggregationOnce(OnboardingRetentionAggregation, onboardingRetentionAggConfig),
                    );
                });

                const results = await Promise.all(aggregationArray);

                // Ensure that you assign any new agg an id value in the agg build function
                const resultsMap = results.reduce((acc, result, idx) => {
                    const { id } = result || null;
                    if (!id) {
                        acc[`unknownAgg${idx}`] = result;
                    } else {
                        acc[id] = result;
                    }

                    return acc;
                }, {});

                const onboardingEventMetrics = {
                    perEventMetrics: {},
                    startFirstPeriod: '',
                    endFirstPeriod: '',
                    startSecondPeriod: '',
                    endSecondPeriod: '',
                    endPeriod: '',
                    startPeriod: '',
                    segmentName: segment?.name || '',
                };
                Object.keys(onboardingEvents).forEach((selectedEventId) => {
                    const selectedEvent = onboardingEvents[selectedEventId];
                    const { appDisplayName, name: onboardingEventName } = selectedEvent;
                    const appName = appDisplayName || ''; // Onboarding Event App Name
                    const keyOnboardingEvent = onboardingEventName || ''; // Onboarding Page/Feature Name
                    const onboardingSuccessEventDescription = 'This event was super successful'; // Hardcoded for now

                    const {
                        percentageOnboardedRetained,
                        percentageNotOnboardedRetained,
                        onboardedNumRetained,
                    } = resultsMap[`onboardingRetentionAggregation_${selectedEventId}`];
                    const retentionAgg = {
                        completedOnboardingRetentionPercent: roundPercentage(percentageOnboardedRetained),
                        incompleteOnboardingRetentionPercent: roundPercentage(percentageNotOnboardedRetained),
                        numRetainedUsers: onboardedNumRetained,
                    };

                    const { totalNewFirstComparisonRange, firstMonth, lastMonth } = resultsMap[
                        `totalOnboardedAggregation_${selectedEventId}`
                    ];
                    const endPeriod = firstMonth; // Question 5 - Current date minus selected date range
                    const startPeriod = lastMonth; // Question 5 - Current data (month/year)
                    const timeToCompleteMetrics = this.timeToCompleteMetrics(
                        resultsMap[`onboardingTimeToCompleteAggregation_${selectedEventId}`],
                    );
                    const onboardingMetrics = this.onboardingMetrics(
                        resultsMap[`percentageOnboardedAggregation_${selectedEventId}`],
                    );
                    const { startFirstPeriod, endFirstPeriod, startSecondPeriod, endSecondPeriod } = resultsMap[
                        `percentageOnboardedAggregation_${selectedEventId}`
                    ];
                    const retentionMetrics = this.retentionMetrics(retentionAgg);

                    // On first run, set the period dates as they are shared across all aggs and events.
                    if (onboardingEventMetrics.startFirstPeriod === '') {
                        onboardingEventMetrics.startFirstPeriod = startFirstPeriod;
                        onboardingEventMetrics.endFirstPeriod = endFirstPeriod;
                        onboardingEventMetrics.startSecondPeriod = startSecondPeriod;
                        onboardingEventMetrics.endSecondPeriod = endSecondPeriod;
                        onboardingEventMetrics.endPeriod = endPeriod;
                        onboardingEventMetrics.startPeriod = startPeriod;
                        onboardingEventMetrics.metricType = onboardingMetrics.type;
                    }

                    onboardingEventMetrics.perEventMetrics[selectedEventId] = {
                        appName,
                        keyOnboardingEvent,
                        onboardingSuccessEventDescription,
                        totalNewVisitorsFirstComparisonRange: smartFormat(totalNewFirstComparisonRange || 0),
                        totalNewAccountsFirstComparisonRange: smartFormat(totalNewFirstComparisonRange || 0),
                        timeToCompleteMetrics,
                        onboardingMetrics,
                        retentionMetrics,
                    };
                });

                let visitorsInfluenced = 0;
                let accountsInfluenced = 0;

                // ONBOARDING
                const { sumViewCount, sumVisitorCount, sumAccountCount } = resultsMap.impactBriefGuideMetricsAgg;

                const viewCounts = this.usesAccountsAggregation
                    ? {
                        sumViewCount,
                        totalAccountsOrVisitors: sumAccountCount,
                        type: 'account',
                    }
                    : {
                        sumViewCount,
                        totalAccountsOrVisitors: sumVisitorCount,
                        type: 'visitor',
                    };

                // GUIDE GOALS
                if (numberOfGuidesWithGoals) {
                    const {
                        totalVisitorsInfluenced,
                        totalAccountsInfluenced,
                    } = resultsMap.totalVisitorsAndAccountsInfluencedAggregation;
                    visitorsInfluenced = totalVisitorsInfluenced;
                    accountsInfluenced = totalAccountsInfluenced;
                }

                const influencedMetrics = this.usesAccountsAggregation
                    ? {
                        count: accountsInfluenced,
                        type: 'account',
                    }
                    : {
                        count: visitorsInfluenced,
                        type: 'visitor',
                    };

                const companyName = this.subscriptionDisplayName; // Subscription Display Name
                const guides = guideNames; // Pull guides from form
                const guideCostSavings = formatSmartMoney(guides.length * IMPACT_BRIEF_COST_SAVINGS);
                const engineeringHoursSaved = guides.length * 6; // from the doc references, 9 hours w/o 3 w/
                const showAscendingSuggestedActions = this.showAscendingSuggestedActions(numEventsSelected);

                return generateBlob({
                    title: `${companyName} Impact Brief`,
                    subtitle: "Discover how we've improved onboarding and user experience with Pendo",
                    startPeriod: onboardingEventMetrics.startPeriod,
                    endPeriod: onboardingEventMetrics.endPeriod,
                    author: this.userName,
                    content: {
                        companyName,
                        onboardingEventMetrics,
                        expectedDaysToOnboard: this.onboardingLength,
                        numberOfGuidesWithGoals,
                        guideCostSavings,
                        engineeringHoursSaved,
                        numberOfGuides: guides.length,
                        feedbackResponses,
                        guides,
                        influencedMetrics,
                        viewCounts,
                        dayCount,
                        showAscendingSuggestedActions,
                        totalNewVisitorsFirstComparisonRange:
                            onboardingEventMetrics.totalNewVisitorsFirstComparisonRange,
                        totalNewAccountsFirstComparisonRange:
                            onboardingEventMetrics.totalNewAccountsFirstComparisonRange,
                        startFirstPeriod: onboardingEventMetrics.startFirstPeriod,
                        endFirstPeriod: onboardingEventMetrics.endFirstPeriod,
                        startSecondPeriod: onboardingEventMetrics.startSecondPeriod,
                        endSecondPeriod: onboardingEventMetrics.endSecondPeriod,
                        startPeriod: onboardingEventMetrics.startPeriod,
                        endPeriod: onboardingEventMetrics.endPeriod,
                    },
                });
            } catch {
                PendoNotification({
                    type: 'error',
                    title: 'Preview generation failed',
                    message: 'Please try again. If the issue keeps happening, contact Pendo Support.',
                });
                this.isLoading = false;

                return null;
            }
        },
        timeToCompleteMetrics ({
            pastMedianTimeToComplete,
            presentMedianTimeToComplete,
            timeToCompletePercentageChange,
        }) {
            const timeDifference = pastMedianTimeToComplete - presentMedianTimeToComplete;
            const isSignificantChange = Math.abs(timeDifference) > 0.5;

            let timeToCompleteSentiment = 'neutral';
            if (isSignificantChange) {
                timeToCompleteSentiment = timeDifference > 0 ? 'positive' : 'negative';
            }

            if (timeToCompleteSentiment === 'positive') {
                this.numberOfPositiveMetrics++;
            }

            return {
                timeToValueFirstComparisonRange: formatTimeToComplete(pastMedianTimeToComplete),
                timeToValueSecondComparisonRange: formatTimeToComplete(presentMedianTimeToComplete),
                timeToValuePercentComparison: roundPercentage(Math.abs(timeToCompletePercentageChange)),
                sentiment: timeToCompleteSentiment,
            };
        },
        onboardingMetrics ({
            percentBefore,
            percentAfter,
            completedCountBefore,
            completedCountAfter,
            newCountBefore,
            newCountAfter,
        }) {
            const percentageCompleteFirstComparisonRange = roundPercentage(percentBefore);
            const percentageCompleteSecondComparisonRange = roundPercentage(percentAfter);
            const onboardingPercentChange = roundPercentage(
                percentageCompleteSecondComparisonRange - percentageCompleteFirstComparisonRange,
            );

            const isSignificantChange = Math.abs(onboardingPercentChange) > 0.5;
            let onboardingSentiment = 'neutral';
            if (isSignificantChange) {
                onboardingSentiment = onboardingPercentChange > 0 ? 'positive' : 'negative';
            }

            if (onboardingSentiment === 'positive') {
                this.numberOfPositiveMetrics++;
            }

            return {
                onboardingPercentChange,
                percentageCompleteFirstComparisonRange,
                percentageCompleteSecondComparisonRange,
                completedCountBefore,
                completedCountAfter,
                newCountBefore,
                newCountAfter,
                type: this.usesAccountsAggregation ? 'accounts' : 'users',
                sentiment: onboardingSentiment,
            };
        },

        retentionMetrics ({
            completedOnboardingRetentionPercent,
            incompleteOnboardingRetentionPercent,
            numRetainedUsers,
        }) {
            const retentionPercentGoalThreshold = 80;
            const retentionPercentGoalIncrease = 20;

            // If the completed onboarding retention percent is greater than or equal to the threshold, set the goal to 100.
            const onboardingRetentionPercentGoal =
                completedOnboardingRetentionPercent >= retentionPercentGoalThreshold
                    ? 100
                    : completedOnboardingRetentionPercent + retentionPercentGoalIncrease;
            const retentionTotalUsersCount = numRetainedUsers;
            const retentionEstimatedUsersCount = Math.round(retentionTotalUsersCount * 0.2 * 10) / 10;

            const retentionDifference = completedOnboardingRetentionPercent - incompleteOnboardingRetentionPercent;
            const isSignificantChange = Math.abs(retentionDifference) > 0.5;
            let retentionSentiment = 'neutral';
            if (isSignificantChange) {
                retentionSentiment = retentionDifference > 0 ? 'positive' : 'negative';
            }

            if (retentionSentiment === 'positive') {
                this.numberOfPositiveMetrics++;
            }

            return {
                completedOnboardingRetentionPercent,
                incompleteOnboardingRetentionPercent,
                onboardingRetentionPercentDifference: roundPercentage(retentionDifference),
                onboardingRetentionPercentGoal,
                retentionTotalUsersCount,
                retentionEstimatedUsersCount,
                sentiment: retentionSentiment,
                onboardingLength: this.onboardingLength,
            };
        },
        generateFilterRanges (dateRange) {
            const { count: dayCount } = dateRange.spec;
            const { type } = dateRange.config;
            const isCustom = type === 'Custom';
            let filterPeriodBase;
            let filterPeriodPast;
            let filterPeriodPresent;

            if (isCustom) {
                const { first } = dateRange.spec;
                filterPeriodBase = `startOfPeriod("daily", date("${first}"))`;
                filterPeriodPast = `dateAdd(startOfPeriod("daily", date("${first}")), -${dayCount}, "days")`;
                filterPeriodPresent = `dateAdd(startOfPeriod("daily", date("${first}")), ${dayCount}, "days")`;
            } else {
                filterPeriodBase = `dateAdd(startOfPeriod("daily", now()), -${dayCount}, "days")`;
                filterPeriodPast = `dateAdd(startOfPeriod("daily", now()), -${dayCount * 2}, "days")`;
                filterPeriodPresent = 'dateAdd(startOfPeriod("daily", now()), -1, "days")';
            }

            return {
                filterPeriodBase,
                filterPeriodPast,
                filterPeriodPresent,
            };
        },
        showAscendingSuggestedActions (numEventsSelected) {
            const positiveThreshold = SUGGESTED_ACTIONS_THRESHOLDS.POSITIVE[numEventsSelected];

            return this.numberOfPositiveMetrics >= positiveThreshold;
        },
        async runTotalOnboardedAggregation (event, filterPeriodBase, filterPeriodPast, filterPeriodPresent) {
            const { appId, kind } = event;

            const totalVisitorsAndAccountsAggConfig = {
                appId,
                targetType: camelCase(kind),
                targetId: event.id,
                firstComparisonRange: filterPeriodBase,
                secondComparisonRange: filterPeriodPast,
                thirdComparisonRange: filterPeriodPresent,
                expectedDaysToOnboard: this.onboardingLength,
                usesAccounts: this.usesAccountsAggregation,
            };

            const result = await runAggregationOnce(TotalOnboardedAggregation, totalVisitorsAndAccountsAggConfig);

            return result;
        },
        async eventsDataCheck () {
            this.isLoading = true;

            this.eventsWithLimitedData = [];

            const { onboardingEvents, briefDateRange, onboardingLength } = this.formData;

            const selectedEvents = Object.values(onboardingEvents);

            const { filterPeriodBase, filterPeriodPast, filterPeriodPresent } = this.generateFilterRanges(
                briefDateRange,
            );

            this.onboardingLength = onboardingLength?.value;

            this.eventsWithLimitedData = await selectedEvents.reduce(async (eventNamesPromise, event) => {
                const eventNames = await eventNamesPromise;
                const result = await this.runTotalOnboardedAggregation(
                    event,
                    filterPeriodBase,
                    filterPeriodPast,
                    filterPeriodPresent,
                );

                if (result.totalNewFirstComparisonRange === 0) {
                    eventNames.push(event.name);
                }

                return eventNames;
            }, Promise.resolve([]));

            this.isLoading = false;
        },
        downloadImpactBrief () {
            if (!this.impactBriefPreview) {
                PendoNotification({
                    type: 'error',
                    title: 'Download failed',
                    message: 'No Impact Brief available to download. Please try generating the brief again.',
                });

                return;
            }
            saveFile(this.impactBriefPreview, `${this.subscriptionDisplayName} Impact Brief.docx`);
            PendoNotification({
                type: 'success',
                title: 'Impact Brief ready',
                message: 'Your Impact Brief has been successfully generated and is now downloaded to your device.',
            });
            this.triggerImpactBriefTrackEvent();
            this.$emit('hideWizard');
        },
        handleExitClicked () {
            cancelPendingAggregations();
            this.$emit('hideWizard');
        },
        triggerImpactBriefTrackEvent () {
            window.pendo.track('Downloaded Impact Brief', {
                comparisonRangeStart: this.formData.briefDateRange.config.start,
                comparisonRangeEnd: this.formData.briefDateRange.config.end,
                comparisonRangeLengthInDays: this.formData.briefDateRange.spec.count,
                expectedDaysToOnboard: this.formData.onboardingLength.value,
                segment: this.formData.segment?.name,
                numberOfGuidesSelected: Object.keys(this.formData.guidePolls).length,
                numberOfOnboardingEventsSelected: Object.keys(this.formData.onboardingEvents).length,
                accountsOrVisitors: this.formData.measureType,
            });
        },
    },
};
</script>
